import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Sensors as SensorsIcon
} from '@mui/icons-material';
import { fetchSensorById, updateSensor, fetchTipoSensor } from '../../../actions/sensor';
import { cambiarEstadoSensor } from '../../../actions/dispositivos';
import DeviceControlButton from '../../../components/device-control-button';
import Bread from '../../../components/breadcrumb';
import { routeSensorEdit } from '../../../utils/routesBread';
import Swal from 'sweetalert2';

import { useModuloPermissions } from '../../../hooks/useModuloPermissions';

const EditSensor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector(state => state.userReducer.user);
  const [sensorData, setSensorData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [tiposSensor, setTiposSensor] = useState([]);
  // Permisos para el módulo sensor
  const permissions = useModuloPermissions('sensor');
  // Debug logs
  console.log('🔍 DEBUG - Usuario:', user);
  console.log('🔍 DEBUG - Rol del usuario:', user?.rol);
  console.log('🔍 DEBUG - esAdmin:', permissions.esAdmin);
  console.log('🔍 DEBUG - esSuperAdmin:', permissions.esSuperAdmin);
  console.log('🔍 DEBUG - puedeActualizar:', permissions.puedeActualizar);
  console.log('🔍 DEBUG - isEditing:', isEditing);
  console.log('🔍 DEBUG - editedData.alwaysOn:', editedData.alwaysOn);

  // Función auxiliar para obtener el ID del tipo de sensor
  const getTipoSensorId = (tipoSensor) => {
    if (!tipoSensor) return '';
    if (typeof tipoSensor === 'object' && tipoSensor._id) {
      return tipoSensor._id;
    }
    if (typeof tipoSensor === 'string') {
      return tipoSensor;
    }
    return '';
  };

  useEffect(() => {
    loadSensorData();
    loadTiposSensor();
  }, [id]);

  const loadTiposSensor = async () => {
    try {
      const result = await dispatch(fetchTipoSensor());
      if (result.payload?.data) {
        console.log('Tipos de sensor cargados:', result.payload.data);
        setTiposSensor(result.payload.data);
      }
    } catch (error) {
      console.error('Error loading tipos de sensor:', error);
    }
  };

  const loadSensorData = async () => {
    try {
      setLoading(true);
      const result = await dispatch(fetchSensorById(id));
      if (result.payload?.data) {
        const sensor = result.payload.data;
        console.log('Sensor cargado:', sensor);
        console.log('Tipo de sensor:', sensor.tipo);
        console.log('Tipo de sensor ID:', getTipoSensorId(sensor.tipo));
        setSensorData(sensor);
        
        setEditedData({
          nombre: sensor.nombre || '',
          alias: sensor.alias || '',
          unidades: sensor.unidades || '',
          tipoSensor: getTipoSensorId(sensor.tipo),
          rangoMin: sensor.rangoMin?.$numberDecimal || sensor.rangoMin || '',
          rangoMax: sensor.rangoMax?.$numberDecimal || sensor.rangoMax || '',
          resolucionMin: sensor.resolucionMin?.$numberDecimal || sensor.resolucionMin || '',
          resolucionMax: sensor.resolucionMax?.$numberDecimal || sensor.resolucionMax || '',
          peligrosidad: sensor.peligrosidad?.$numberDecimal || sensor.peligrosidad || '',
          peligrosidadAjuste: sensor.peligrosidadAjuste?.$numberDecimal || sensor.peligrosidadAjuste || '',
          posicionEnTrama: sensor.posicionEnTrama || '',
          consumo: sensor.consumo || false,
          estatusDispositivo: sensor.estatusDispositivo !== undefined ? sensor.estatusDispositivo : true,
          alwaysOn: sensor.alwaysOn || false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la información del sensor'
        });
        navigate('/sensores');
      }
    } catch (error) {
      console.error('Error loading sensor:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al cargar el sensor: ' + error.message
      });
      navigate('/sensores');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo si existe
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!editedData.nombre?.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }
    
    if (!editedData.alias?.trim()) {
      newErrors.alias = 'El alias es requerido';
    }
    
    if (!editedData.unidades?.trim()) {
      newErrors.unidades = 'Las unidades son requeridas';
    }
    
    if (!editedData.tipoSensor?.trim()) {
      newErrors.tipoSensor = 'El tipo de sensor es requerido';
    }
    
    // Validar rangos numéricos
    const numericFields = ['rangoMin', 'rangoMax', 'resolucionMin', 'resolucionMax', 'peligrosidad', 'peligrosidadAjuste'];
    numericFields.forEach(field => {
      if (editedData[field] && isNaN(Number(editedData[field]))) {
        newErrors[field] = 'Debe ser un número válido';
      }
    });
    
    // Validar que rangoMin < rangoMax
    if (editedData.rangoMin && editedData.rangoMax) {
      if (Number(editedData.rangoMin) >= Number(editedData.rangoMax)) {
        newErrors.rangoMax = 'El rango máximo debe ser mayor al mínimo';
      }
    }
    
    // Validar que resolucionMin < resolucionMax
    if (editedData.resolucionMin && editedData.resolucionMax) {
      if (Number(editedData.resolucionMin) >= Number(editedData.resolucionMax)) {
        newErrors.resolucionMax = 'La resolución máxima debe ser mayor a la mínima';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      // Preparar datos para el servidor
      const updateData = {
        ...editedData,
        tipo: editedData.tipoSensor, // Mapear tipoSensor a tipo que espera el backend
        rangoMin: Number(editedData.rangoMin),
        rangoMax: Number(editedData.rangoMax),
        resolucionMin: Number(editedData.resolucionMin),
        resolucionMax: Number(editedData.resolucionMax),
        peligrosidad: Number(editedData.peligrosidad),
        peligrosidadAjuste: Number(editedData.peligrosidadAjuste),
        posicionEnTrama: Number(editedData.posicionEnTrama)
      };
      
      // Remover tipoSensor del objeto porque ya se mapeó a tipo
      delete updateData.tipoSensor;

      const result = await dispatch(updateSensor(id, updateData));
      
      if (result.data) {
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Sensor actualizado correctamente'
        });
        setIsEditing(false);
        await loadSensorData(); // Recargar datos
      } else {
        throw new Error('Error al actualizar el sensor');
      }
    } catch (error) {
      console.error('Error saving sensor:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al actualizar el sensor: ' + error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAlwaysOnUpdate = async (newValue) => {
    try {
      const updateData = { alwaysOn: newValue };
      await dispatch(updateSensor(id, updateData));
      await loadSensorData();
    } catch (error) {
      console.error('Error updating alwaysOn:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al actualizar la configuración: ' + error.message
      });
    }
  };

  const handleCambiarEstadoSensor = async (sensorId, nuevoEstado) => {
    try {
      await dispatch(cambiarEstadoSensor(sensorId, nuevoEstado));
      await loadSensorData(); // Recargar datos para reflejar el cambio

      // Actualizar también editedData para que la validación funcione en tiempo real
      setEditedData(prev => ({
        ...prev,
        estatusDispositivo: nuevoEstado
      }));
    } catch (error) {
      console.error('Error al cambiar estado del sensor:', error);
      // Re-lanzar el error para que el DeviceControlPanel lo maneje
      throw error;
    }
  };

  const handleAlwaysOnChange = (newValue) => {
    // Si se intenta marcar como crítico (alwaysOn = true) pero el dispositivo está apagado
    if (newValue && !editedData.estatusDispositivo) {
      Swal.fire({
        icon: 'warning',
        title: 'Dispositivo Apagado',
        text: 'Para marcar un sensor como crítico, primero debe estar encendido. Por favor, encienda el dispositivo antes de continuar.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ff9800'
      });
      return; // No permitir la acción
    }

    const title = newValue ? 'Activar Dispositivo Crítico' : 'Desactivar Dispositivo Crítico';
    const text = newValue
      ? 'Este sensor se marcará como crítico. ¿Está seguro de que desea continuar?'
      : 'Este sensor ya no será considerado crítico. ¿Está seguro de que desea continuar?';

    Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: newValue ? '#ff9800' : '#4caf50',
      cancelButtonColor: '#d33'
    }).then((result) => {
      if (result.isConfirmed) {
        handleInputChange('alwaysOn', newValue);
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!sensorData) {
    return (
      <Box p={3}>
        <Alert severity="error">
          No se pudo cargar la información del sensor
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Bread routes={routeSensorEdit} />
      
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Fab 
          size="small" 
          onClick={() => navigate('/sensor')}
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
            }
          }}
        >
          <ArrowBackIcon />
        </Fab>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          {isEditing ? 'Editando Sensor' : 'Detalles del Sensor'}
        </Typography>
        {sensorData.alwaysOn && (
          <Chip 
            icon={<SecurityIcon />} 
            label="Crítico" 
            color="warning" 
            variant="outlined"
            size={isSmallScreen ? 'small' : 'medium'}
          />
        )}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        alignItems: 'flex-start',
        flexDirection: { xs: 'column', lg: 'row' }
      }}>
        {/* Información básica y configuración */}
        <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
          {/* Información Básica */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Información Básica
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                {permissions.puedeActualizar && (
                  !isEditing ? (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      size={isSmallScreen ? 'small' : 'medium'}
                    >
                      Editar
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setIsEditing(false);
                          setEditedData({
                            nombre: sensorData.nombre || '',
                            alias: sensorData.alias || '',
                            unidades: sensorData.unidades || '',
                            tipoSensor: getTipoSensorId(sensorData.tipo),
                            rangoMin: sensorData.rangoMin?.$numberDecimal || sensorData.rangoMin || '',
                            rangoMax: sensorData.rangoMax?.$numberDecimal || sensorData.rangoMax || '',
                            resolucionMin: sensorData.resolucionMin?.$numberDecimal || sensorData.resolucionMin || '',
                            resolucionMax: sensorData.resolucionMax?.$numberDecimal || sensorData.resolucionMax || '',
                            peligrosidad: sensorData.peligrosidad?.$numberDecimal || sensorData.peligrosidad || '',
                            peligrosidadAjuste: sensorData.peligrosidadAjuste?.$numberDecimal || sensorData.peligrosidadAjuste || '',
                            posicionEnTrama: sensorData.posicionEnTrama || '',
                            consumo: sensorData.consumo || false,
                            estatusDispositivo: sensorData.estatusDispositivo || false,
                            alwaysOn: sensorData.alwaysOn || false
                          });
                          setErrors({});
                        }}
                        disabled={saving}
                        size={isSmallScreen ? 'small' : 'medium'}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        size={isSmallScreen ? 'small' : 'medium'}
                      >
                        {saving ? 'Guardando...' : 'Guardar'}
                      </Button>
                    </>
                  )
                )}
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={editedData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.nombre}
                  helperText={errors.nombre}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alias"
                  value={editedData.alias}
                  onChange={(e) => handleInputChange('alias', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.alias}
                  helperText={errors.alias}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Unidades"
                  value={editedData.unidades}
                  onChange={(e) => handleInputChange('unidades', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.unidades}
                  helperText={errors.unidades}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth 
                  error={!!errors.tipoSensor}
                  disabled={!isEditing}
                  size={isSmallScreen ? 'small' : 'medium'}
                >
                  <InputLabel>Tipo de Sensor</InputLabel>
                  <Select
                    value={editedData.tipoSensor || ''}
                    onChange={(e) => handleInputChange('tipoSensor', e.target.value)}
                    label="Tipo de Sensor"
                  >
                    <MenuItem value="">
                      <em>Selecciona un tipo de sensor</em>
                    </MenuItem>
                    {tiposSensor.map((tipo) => (
                      <MenuItem key={tipo._id} value={tipo._id}>
                        {tipo.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tipoSensor && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      {errors.tipoSensor}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Posición en Trama"
                  type="number"
                  value={editedData.posicionEnTrama}
                  onChange={(e) => handleInputChange('posicionEnTrama', e.target.value)}
                  disabled={!isEditing}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedData.consumo}
                      onChange={(e) => handleInputChange('consumo', e.target.checked)}
                      disabled={!isEditing}
                      size={isSmallScreen ? 'small' : 'medium'}
                    />
                  }
                  label="Sensor de Consumo"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
                />
              </Grid>
              
              {(permissions.esAdmin || permissions.esSuperAdmin || permissions.puedeActualizar) && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedData.alwaysOn}
                        onChange={(e) => handleAlwaysOnChange(e.target.checked)}
                        disabled={!isEditing}
                        color="warning"
                        size={isSmallScreen ? 'small' : 'medium'}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="warning" fontSize="small" />
                        <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                          Dispositivo Crítico
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Configuración de Rangos */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Configuración de Rangos
            </Typography>
            
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rango Mínimo"
                  type="number"
                  value={editedData.rangoMin}
                  onChange={(e) => handleInputChange('rangoMin', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.rangoMin}
                  helperText={errors.rangoMin}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rango Máximo"
                  type="number"
                  value={editedData.rangoMax}
                  onChange={(e) => handleInputChange('rangoMax', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.rangoMax}
                  helperText={errors.rangoMax}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Resolución Mínima"
                  type="number"
                  value={editedData.resolucionMin}
                  onChange={(e) => handleInputChange('resolucionMin', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.resolucionMin}
                  helperText={errors.resolucionMin}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Resolución Máxima"
                  type="number"
                  value={editedData.resolucionMax}
                  onChange={(e) => handleInputChange('resolucionMax', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.resolucionMax}
                  helperText={errors.resolucionMax}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Peligrosidad"
                  type="number"
                  value={editedData.peligrosidad}
                  onChange={(e) => handleInputChange('peligrosidad', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.peligrosidad}
                  helperText={errors.peligrosidad}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ajuste de Peligrosidad"
                  type="number"
                  value={editedData.peligrosidadAjuste}
                  onChange={(e) => handleInputChange('peligrosidadAjuste', e.target.value)}
                  disabled={!isEditing}
                  error={!!errors.peligrosidadAjuste}
                  helperText={errors.peligrosidadAjuste}
                  size={isSmallScreen ? 'small' : 'medium'}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Sección Solo para Administradores */}
          {(permissions.esAdmin || permissions.esSuperAdmin) && false && (
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f5f5f5' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <SecurityIcon color="warning" />
                <Typography variant="h6" color="warning.main" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Panel de Administrador
                </Typography>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Esta sección solo es visible para administradores y superadministradores.
              </Alert>
              
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Configuración de Dispositivo Crítico
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sensorData.alwaysOn}
                        onChange={(e) => handleAlwaysOnUpdate(e.target.checked)}
                        color="warning"
                        size={isSmallScreen ? 'small' : 'medium'}
                      />
                    }
                    label="Marcar como dispositivo crítico"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                    Los dispositivos críticos no pueden ser apagados por los usuarios normales.
                  </Typography>
                </CardContent>
              </Card>
            </Paper>
          )}
        </Box>

        {/* Panel de Control del Dispositivo */}
        <Box sx={{
          width: { xs: '100%', lg: '400px' },
          flexShrink: 0,
          ml: { xs: 0, lg: 'auto' }
        }}>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              <SensorsIcon />
              Control de Dispositivo
            </Typography>

            {permissions.puedeControlar ? (
              <DeviceControlButton
                dispositivo={sensorData}
                tipo="sensor"
                onCambiarEstado={handleCambiarEstadoSensor}
                showFullPanel={true}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Estado actual:
                  </Typography>
                  <Chip
                    label={sensorData.estatusDispositivo ? 'Encendido' : 'Apagado'}
                    color={sensorData.estatusDispositivo ? 'success' : 'error'}
                    size="small"
                    icon={<SensorsIcon />}
                  />
                </Box>

                <Alert severity="warning" size="small">
                  <Typography variant="caption">
                    No tienes permisos para controlar este dispositivo.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default EditSensor;
